import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { inject, Injector, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { ToastService } from "@siemens/ix-angular";
import { catchError, from, mergeMap, Observable, throwError } from "rxjs";
import { ApiException } from "src/app/api/client";
// import { ApiException } from "src/app/api/client";

/** Display the http error message on a Toast */
export const httpErrorInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const ngZone = inject(NgZone);
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse || (err && ApiException.isApiException(err))) {
        // Redirect Route
        if (Number(err.status) === HttpStatusCode.Unauthorized) {
          return from(ngZone.run(() => injector.get(Router).navigate(["/unauthorized"], { skipLocationChange: true }))).pipe(
            catchError(() => throwError(() => err)),
            mergeMap(() => throwError(() => err))
          );
        }

        // Don't display Toast
        // if (err.status === HttpStatusCode.NotFound && [].some((u) => request.url.includes(u))) {
        //   return throwError(() => err);
        // }

        const maybeBlob: unknown = err[<keyof object>"error"];
        if (maybeBlob && maybeBlob instanceof Blob) {
          return from(
            maybeBlob
              .text()
              .then((text) => JSON.parse(text))
              .catch(() => null)
          ).pipe(
            mergeMap((parsed: unknown) => {
              if (parsed && typeof parsed === "object") {
                const message = parsed[<keyof object>"Message"] || parsed[<keyof object>"message"];
                if (message && typeof message === "string") {
                  const parsedStatus = parsed[<keyof object>"Status"] || parsed[<keyof object>"status"];
                  const status =
                    hasStatus(err) && err.status > 0
                      ? err.status
                      : parsedStatus && typeof parsedStatus === "number" && parsedStatus > 0
                        ? parsedStatus
                        : undefined;

                  injector.get(ToastService, null, { optional: true })?.show({
                    title: status ? status.toString() : undefined,
                    message: message,
                    autoCloseDelay: 8000,
                    type: "error",
                  });

                  return throwError(() => err);
                }
              }
              return baseMessageToast(err, injector);
            })
          );
        }
      }

      return baseMessageToast(err, injector);
    })
  );
};

const hasStatus = (err: unknown): err is { status: number } => {
  return typeof err === "object" && err !== null && "status" in err && typeof (<{ status: unknown }>err).status === "number";
};

const hasMessage = (err: unknown): err is { message: string } => {
  return typeof err === "object" && err !== null && "message" in err && typeof (<{ message: unknown }>err).message === "string";
};

const baseMessageToast = (err: unknown, injector: Injector): Observable<never> => {
  const status = hasStatus(err) ? err.status : undefined;

  injector.get(ToastService, null, { optional: true })?.show({
    title: status && status > 0 ? status.toString() : undefined,
    message: `${hasMessage(err) ? err.message : err}`,
    autoCloseDelay: 8000,
    type: "error",
  });

  return throwError(() => err);
};
